import type { ICellRendererComp, ICellRendererParams } from "ag-grid-enterprise";

interface LinkCellRendererParams extends ICellRendererParams {
  // Defaults to false: renders like plain text (no underline/link color)
  styled?: boolean;
  // Row-data field holding the link target. Defaults to "edit_url" so existing
  // callers (e.g. the organizations grid) are unaffected.
  urlField?: string;
  // Defaults to false: opens in the same tab. Set true to open the link in a
  // new tab (target="_blank" rel="noopener") — e.g. jumping from a ledger row
  // to an election without losing the grid's place.
  newTab?: boolean;
}

/**
 * LinkCellRenderer — renders a cell's value as a clickable link when an accompanying
 * URL field is present on the row, falling back to plain text otherwise. Looks like
 * plain text by default; pass `styled: true` to render as a normal link.
 *
 * Reads:
 * - `params.value` — the display text (e.g. the row's name)
 * - `params.data[params.urlField ?? "edit_url"]` — the link target; when
 *   null/undefined, falls back to plain text.
 * - `params.styled` — set via `innerRendererParams: { styled: true }` (when used
 *   as an `innerRenderer`) or `cellRendererParams: { styled: true }` (when used
 *   directly as a column's `cellRenderer`) to keep normal link styling.
 * - `params.urlField` / `params.newTab` — set the same way, via
 *   `cellRendererParams` (or `innerRendererParams`).
 */
export class LinkCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: LinkCellRendererParams): void {
    this.eGui = document.createElement("span");

    const urlField = params.urlField ?? "edit_url";
    const url = (params.data as Record<string, unknown> | undefined)?.[urlField] as string | undefined | null;
    const value = params.value ?? "";

    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.textContent = value;
      link.draggable = false; // avoid hijacking AG Grid's cell range drag-select
      if (params.newTab) {
        link.target = "_blank";
        link.rel = "noopener";
      }
      if (!params.styled) {
        link.style.color = "inherit";
        link.style.textDecoration = "none";
      }
      this.eGui.appendChild(link);
    } else {
      this.eGui.textContent = value;
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }
}
